import React, {Component} from 'react';
import {Chart} from 'react-chartjs-2';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import Util from '../../../../../forecast-app/shared/util/util';
import moment from 'moment';
import RevenueChart from './budget_charts/revenue_chart';
import CostChart from './budget_charts/cost_chart';
import ProfitChart from './budget_charts/profit_chart';
import ActualVarianceToPlanChart from './budget_charts/actual_variance_to_plan_chart';
import ForecastVarianceToPlanChart from './budget_charts/forecast_variance_to_plan_chart';
import InvoiceChart from './budget_charts/invoice_chart';
import TimeChart from './budget_charts/time_chart';
import InvoiceVarianceToActuals from './budget_charts/invoice_variance_to_actuals_chart';
import BaselineVarianceToActuals from './budget_charts/baseline_variance_to_actuals_chart';
import BaselineVarianceToForecast from './budget_charts/baseline_variance_to_forecast_chart';
import {getChartDataByDay} from './budget_charts/budget_charts_util';
import {BUDGET_V2_CHART_TYPE, ESTIMATION_UNIT, TABLE_TABS} from '../../../../../constants';
import GenericEmptyState from '../../../../empty-state/generic_empty_state_v2';
import {budgetChartPlugins} from './budget_charts/chart_plugins';
import * as tracking from '../../../../../tracking';
import {BUDGET_CHART_TRACKING_LABELS} from '../budget_constants';

class BudgetCharts extends Component {
	constructor(props) {
		super(props);

		// Remove the default margin bottom from the chart
		Chart.Legend.prototype.afterFit = function () {
			// This is a pretty gross hack, and was affecting Legends in the entire platform.
			// Added option to enable this hack and use it here, but nowhere else.
			if (this.options.budgetHeightHack) {
				this.height -= 29;
			}
		};
		this.state = {
			activeChart: null, // Make the Revenue Chart active by default
		};

		this.getChartOptions = this.getChartOptions.bind(this);
		this.getSelectedTableTabData = this.getSelectedTableTabData.bind(this);
		this.goToRateCard = this.goToRateCard.bind(this);
	}

	componentDidMount() {
		if (this.charts) {
			const {activeChart} = this.state;

			const chart = this.charts.find(item => item.shouldRender);
			const activeChartTablesTab = localStorage.getItem('activeChartTablesTabBudgetV2') ? JSON.parse(localStorage.getItem('activeChartTablesTabBudgetV2')) : {};
			const chartToRender = activeChartTablesTab[this.props.isMoneyTabSelected ? this.props.selectedTablesTab : this.props.selectedTablesTab + '_time'];

			if (chartToRender && this.charts.find(chart => chart.id === chartToRender)) {
				this.setActiveChart(chartToRender);
			} else if (chart && chart.id !== activeChart) {
				this.setActiveChart(chart.id);
			}
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.charts) {
			const activeChartTablesTab = localStorage.getItem('activeChartTablesTabBudgetV2') ? JSON.parse(localStorage.getItem('activeChartTablesTabBudgetV2')) : {};
			const chartToRender = activeChartTablesTab[this.props.isMoneyTabSelected ? this.props.selectedTablesTab : this.props.selectedTablesTab + '_time'];

			if (prevProps.selectedTablesTab !== this.props.selectedTablesTab && chartToRender) {
				this.setState({activeChart: chartToRender});
			} else if (prevProps.isMoneyTabSelected !== this.props.isMoneyTabSelected && chartToRender) {
				this.setActiveChart(chartToRender);
			} else if (prevProps.selectedTablesTab !== this.props.selectedTablesTab && !chartToRender) {
				const firstChartInList = this.charts.find(item => item.shouldRender);
				if (firstChartInList) this.setActiveChart(firstChartInList.id);
			}
		}
	}

	getChartOptions() {
		const {isMoneyTabSelected, currencySymbol, project, estimationUnit, periodInTable, isShowingWeekNumber, isEstimatedInNewPoints} = this.props;
		const {activeChart} = this.state;
		const {formatNumber, formatMessage} = this.props.intl;
		const chart = this.charts.find(chart => chart.id === activeChart) || null;
		const period = periodInTable.length > 0 ? periodInTable.slice(0, -1) : periodInTable;
		const projectStartDate = Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay);
		const projectEndDate = Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay);
		const projectFirstDate = Util.CreateNonUtcMomentDate(project.projectFirstDateYear, project.projectFirstDateMonth, project.projectFirstDateDay);
		const projectLastDate = Util.CreateNonUtcMomentDate(project.projectLastDateYear, project.projectLastDateMonth, project.projectLastDateDay);

		const hasItemsOutsideProjectPeriod =
			projectStartDate && //
			projectEndDate && //
			(projectStartDate.isAfter(projectFirstDate) || projectEndDate.isBefore(projectLastDate));
		return {
			budgetHeightHack: true,
			elements: {
				line: {
					tension: 0,
				},
			},
			tooltips: {
				mode: 'x-axis',
				backgroundColor: '#fff',
				borderColor: '#e6e6e6',
				borderWidth: 1,
				fontFamily: 'neue-haas-grotesk-text',
				titleFontColor: '#535353',
				bodyFontColor: '#535353',
				footerFontColor: '#535353',
				xPadding: 12,
				yPadding: 12,
				callbacks: {
					title: tooltipItem => {
						const date = moment(tooltipItem[0].label);
						const dateLabel = date.format('DD MMMM, YYYY');
						let prefix = '';
						if (date.isSame(projectStartDate)) {
							prefix = formatMessage({id: 'project_budget.project_start_date'}) + ': ';
						}
						if (date.isSame(projectEndDate)) {
							prefix = formatMessage({id: 'project_budget.project_end_date'}) + ': ';
						}
						return `${prefix}${dateLabel}`;
					},
					footer: tooltipItems => {
						if (hasItemsOutsideProjectPeriod) {
							const date = moment(tooltipItems[0].label);

							return projectStartDate.isAfter(date) || projectEndDate.isBefore(date) ? formatMessage({id: 'project_budget.outside_project_period_legend'}) : null;
						}
						return null;
					},
					label: (tooltipItem, data) => {
						let labels;
						if (typeof chart.labels === 'function') {
							labels = chart.labels(data.datasets, tooltipItem.datasetIndex);
						} else {
							labels = chart.labels;
						}

						const value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
						let renderedValue = '';
						if (isMoneyTabSelected) {
							renderedValue = Util.getFormattedNumberWithCurrency(currencySymbol, value, this.props.intl);
						} else {
							if (isEstimatedInNewPoints) {
								renderedValue = Util.convertMinutesToFullHour(value * 60, this.props.intl);
							} else {
								if (estimationUnit === ESTIMATION_UNIT.POINTS) {
									renderedValue = `${formatNumber(value)}p`;
								} else {
									renderedValue = Util.convertMinutesToFullHour(value * 60, this.props.intl);
								}
							}
						}

						return `${labels[tooltipItem.datasetIndex]}: ${renderedValue}`;
					},
				},
			},
			layout: {
				padding: {
					left: 0,
					right: 0,
					top: 40,
					bottom: 20,
				},
			},
			animation: {
				easing: 'easeInOutBack',
			},
			legend: {
				position: 'bottom',
			},
			plugins: {
				datalabels: {
					display: false,
				},
				highlightPeriod: {
					startDate: Util.CreateNonUtcMomentDate(project.projectStartYear, project.projectStartMonth, project.projectStartDay),
					endDate: Util.CreateNonUtcMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay),
				},
			},
			hover: {
				intersect: false,
			},
			scales: {
				yAxes: [
					{
						ticks: {
							fontColor: 'rgba(0,0,0,0.5)',
							fontFamily: 'neue-haas-grotesk-text',
							beginAtZero: true,
							maxTicksLimit: 5,
							padding: 20,
							callback: label => {
								return isMoneyTabSelected ? Util.getFormattedCurrencyValue(currencySymbol, label) : `${label}${isEstimatedInNewPoints ? 'h' : estimationUnit === 'POINTS' ? 'p' : 'h'}`;
							},
						},
						gridLines: {
							drawTicks: false,
							display: true,
							borderDash: [8, 4],
						},
					},
				],
				xAxes: [
					{
						type: 'time',
						time: {
							unit: period,
							displayFormats: {
								day: 'DD MMM ',
								week: isShowingWeekNumber ? `[${formatMessage({id: 'overview_time.week'})}] w ` : 'DD MMM ',
								month: 'DD MMM ',
							},
						},
						gridLines: {
							zeroLineColor: 'transparent',
							display: false,
						},
						ticks: {
							autoSkip: true,
							padding: 0,
							fontSize: 11,
							fontColor: 'rgba(0,0,0,0.5)',
							fontFamily: 'neue-haas-grotesk-text',
						},
					},
				],
			},
			responsive: true,
			maintainAspectRatio: false,
		};
	}

	chartClicked(chartId) {
		this.setActiveChart(chartId);

		this.trackGraphClicked(chartId);
	}

	setActiveChart(activeChart) {
		this.setState({activeChart});

		let activeChartTablesTab = localStorage.getItem('activeChartTablesTabBudgetV2') ? JSON.parse(localStorage.getItem('activeChartTablesTabBudgetV2')) : {};
		activeChartTablesTab[this.props.isMoneyTabSelected ? this.props.selectedTablesTab : this.props.selectedTablesTab + '_time'] = activeChart;
		Util.localStorageSetItem('activeChartTablesTabBudgetV2', JSON.stringify(activeChartTablesTab));
	}

	trackGraphClicked(chartId) {
		tracking.trackElementClicked(BUDGET_CHART_TRACKING_LABELS[chartId], {
			Placement: 'Top graph',
		});
	}

	getSelectedTableTabData(workExpensesTabData, workTabData, expensesTabData) {
		const {selectedTablesTab} = this.props;

		switch (selectedTablesTab) {
			case TABLE_TABS.WORK:
				return workTabData;
			case TABLE_TABS.EXPENSES:
				return expensesTabData;
			default:
				// Work & Expenses (default)
				return workExpensesTabData;
		}
	}

	getChartLabels(type) {
		const {formatMessage} = this.props.intl;
		const {billable, isEstimatedInNewPoints, isFixedPrice} = this.props;

		switch (type) {
			case BUDGET_V2_CHART_TYPE.REVENUE: {
				const revenueLabels = [
					formatMessage({id: 'common.projected'}),
					formatMessage({id: 'project_budget.planned_revenue'}),
					formatMessage({id: 'project_budget.actual_revenue'}),
					formatMessage({id: 'settings_subscription.paid'}),
					formatMessage({id: 'common.invoiced'}),
				];

				if (isFixedPrice) {
					revenueLabels.unshift(formatMessage({id: 'common.fixed_price'}));
				}

				return revenueLabels;
			}
			case BUDGET_V2_CHART_TYPE.COST: {
				if (this.props.isEstimatedInNewPoints) {
					return [formatMessage({id: 'insights.component.list.column.actualCost'}), formatMessage({id: 'common.forecast'})];
				}

				return [formatMessage({id: 'project_budget.planned_cost'}), formatMessage({id: 'insights.component.list.column.actualCost'})];
			}
			case BUDGET_V2_CHART_TYPE.PROFIT: {
				const profitLabels = [];
				if (isFixedPrice) {
					profitLabels.push(formatMessage({id: 'project_budget.fixed_price_profit'}));
				} else {
					if (!isEstimatedInNewPoints) {
						profitLabels.push(formatMessage({id: 'project_budget.planned_profit'}));
					}
					profitLabels.push(formatMessage({id: 'project_budget.actual_profit'}));
				}
				if (isEstimatedInNewPoints && !isFixedPrice) {
					profitLabels.push(formatMessage({id: 'project_budget.forecast_profit'}));
				}

				return profitLabels;
			}
			case BUDGET_V2_CHART_TYPE.ACTUAL_VS_PLAN: {
				return [formatMessage({id: 'project_budget.accumulated_variance'}), `${formatMessage({id: 'project_budget.actual_vs_plan'})} (${billable ? formatMessage({id: 'project_budget.revenue'}) : formatMessage({id: 'common.cost'})})`];
			}
			case BUDGET_V2_CHART_TYPE.FORECAST_VS_PLAN: {
				return [formatMessage({id: 'project_budget.accumulated_variance'}), `${formatMessage({id: 'project_budget.forecast_vs_plan'})} (${billable ? formatMessage({id: 'project_budget.revenue'}) : formatMessage({id: 'common.cost'})})`];
			}
			case BUDGET_V2_CHART_TYPE.BASELINE_VS_ACTUAL: {
				return [formatMessage({id: 'project_budget.accumulated_variance'}), `${formatMessage({id: 'project_budget.baseline_vs_actuals'})} (${billable ? formatMessage({id: 'project_budget.revenue'}) : formatMessage({id: 'common.cost'})})`];
			}
			case BUDGET_V2_CHART_TYPE.BASELINE_VS_FORECAST: {
				return [formatMessage({id: 'project_budget.accumulated_variance'}), `${formatMessage({id: 'project_budget.baseline_vs_forecast'})} (${billable ? formatMessage({id: 'project_budget.revenue'}) : formatMessage({id: 'common.cost'})})`];
			}
			case BUDGET_V2_CHART_TYPE.INVOICE: {
				return [formatMessage({id: 'settings_subscription.paid'}), formatMessage({id: 'common.invoiced'})];
			}
			case BUDGET_V2_CHART_TYPE.INVOICE_VS_ACTUAL: {
				return [formatMessage({id: 'project_budget.accumulated_variance'}), formatMessage({id: 'project_budget.invoice_vs_actual'})];
			}
			case BUDGET_V2_CHART_TYPE.TIME: {
				const timeTooltip = [];
				if (!this.props.isEstimatedInNewPoints) {
					timeTooltip.push(formatMessage({id: 'project_budget.planned_time'}));
				}
				timeTooltip.push(formatMessage({id: 'project_budget.actual_time'}));
				return timeTooltip;
			}
			default: {
				return [];
			}
		}
	}

	goToRateCard() {
		this.props.history.push(window.location.pathname.replace('budget', 'rates'));
	}

	render() {
		// Fix for stretched chart when resizing
		for (let id of Object.keys(Chart.instances)) {
			Chart.instances[id].resize();
		}

		const {
			pricePlannedPerDay,
			registeredMinutesPerDay,
			costRemainingPerDay,
			priceActualPerDay,
			plannedMinutesPerDay,
			costActualPerDay,
			costPlannedPerDay,
			priceRemainingPerDay,
			remainingMinutesPerDay,
			invoicedPerDay,
			totalPaymentsPerDay,
			project,
			isMoneyTabSelected,
			pricePlannedWorkPerDay,
			pricePlannedExpensesPerDay,
			priceActualWorkPerDay,
			priceActualExpensesPerDay,
			costPlannedWorkPerDay,
			costPlannedExpensesPerDay,
			costActualWorkPerDay,
			costActualExpensesPerDay,
			priceRemainingWorkPerDay,
			priceRemainingExpensesPerDay,
			costRemainingWorkPerDay,
			costRemainingExpensesPerDay,
			baselinePricePerDay,
			baselineCostPerDay,
			baselinePriceWorkPerDay,
			baselinePriceExpensesPerDay,
			baselineCostWorkPerDay,
			baselineCostExpensesPerDay,
			estimationUnit,
			minutesPerEstimationPoint,
			billable,
			filters,
			showBaseline,
			isFixedPrice,
			isFixedPriceV2,
		} = this.props;

		const projectNumbersFilterOptions = Util.getNestedOptionsObject(filters, 'projectNumbers');
		const projectVariancesFilterOptions = Util.getNestedOptionsObject(filters, 'projectVariances');

		const labels = getChartDataByDay({project, onlyLabels: true});

		const {activeChart} = this.state;

		const {formatMessage} = this.props.intl;

		const {isEstimatedInNewPoints} = this.props;

		// Object functions down below create their own scope, thus overriding the 'this' from our class
		const {getSelectedTableTabData, getChartOptions} = this;

		const plugins = budgetChartPlugins;
		this.charts = [
			{
				id: BUDGET_V2_CHART_TYPE.TIME,
				name: formatMessage({id: 'project-economic.th_time'}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.TIME),
				shouldRender: [!this.props.isInvoiceTableTabSelected, !isMoneyTabSelected].every(condition => condition),
				render: function () {
					return (
						<TimeChart
							data={{
								registeredMinutesPerDay: isEstimatedInNewPoints
									? registeredMinutesPerDay.map(element => ({date: element.date, value: element.value / 60}))
									: estimationUnit === ESTIMATION_UNIT.HOURS
									? registeredMinutesPerDay.map(element => ({date: element.date, value: element.value / 60}))
									: registeredMinutesPerDay.map(element => ({date: element.date, value: element.value / minutesPerEstimationPoint})),
								plannedMinutesPerDay:
									estimationUnit === ESTIMATION_UNIT.HOURS
										? plannedMinutesPerDay.map(element => ({date: element.date, value: element.value / 60}))
										: plannedMinutesPerDay.map(element => ({date: element.date, value: element.value / minutesPerEstimationPoint})),
								project,
								labels,
							}}
							options={getChartOptions()}
							plugins={plugins}
							isEstimatedInNewPoints={isEstimatedInNewPoints}
						/>
					);
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.REVENUE,
				name: formatMessage({id: `project_budget.revenue`}),
				labels: datasets => datasets.map(dataset => dataset.label),
				shouldRender: [billable, projectNumbersFilterOptions.revenue, !this.props.isInvoiceTableTabSelected, isMoneyTabSelected].every(condition => condition),
				render: function () {
					const baselinePerDay = getSelectedTableTabData(baselinePricePerDay, baselinePriceWorkPerDay, baselinePriceExpensesPerDay);
					const forecastPerDay = getSelectedTableTabData(pricePlannedPerDay, pricePlannedWorkPerDay, pricePlannedExpensesPerDay);
					const actualPerDay = getSelectedTableTabData(priceActualPerDay, priceActualWorkPerDay, priceActualExpensesPerDay);
					const remainingPerDay = billable
						? getSelectedTableTabData(priceRemainingPerDay, priceRemainingWorkPerDay, priceRemainingExpensesPerDay)
						: getSelectedTableTabData(costRemainingPerDay, costRemainingWorkPerDay, costRemainingExpensesPerDay);

					return (
						<RevenueChart
							data={{forecastPerDay, actualPerDay, invoicedPerDay, totalPaymentsPerDay, remainingPerDay, baselinePerDay, project, labels}}
							showInvoicing={billable}
							showBaseline={showBaseline}
							options={getChartOptions()}
							plugins={plugins}
							isEstimatedInNewPoints={isEstimatedInNewPoints}
							filters={filters.find(filter => filter.name === 'projectTotals').nestedOptions}
							isFixedPriceV2={isFixedPriceV2}
						/>
					);
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.COST,
				name: formatMessage({id: `common.cost`}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.COST),
				shouldRender: [projectNumbersFilterOptions.cost, !this.props.isInvoiceTableTabSelected, isMoneyTabSelected].every(condition => condition),
				render: function () {
					const forecastPerDay = getSelectedTableTabData(costPlannedPerDay, costPlannedWorkPerDay, costPlannedExpensesPerDay);
					const actualPerDay = getSelectedTableTabData(costActualPerDay, costActualWorkPerDay, costActualExpensesPerDay);
					return <CostChart data={{actualPerDay, forecastPerDay, costRemainingPerDay, project, labels}} options={getChartOptions()} plugins={plugins} isEstimatedInNewPoints={isEstimatedInNewPoints} />;
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.PROFIT,
				name: formatMessage({id: `common.profit`}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.PROFIT),
				shouldRender: [billable, projectNumbersFilterOptions.profit, !this.props.isInvoiceTableTabSelected, isMoneyTabSelected].every(condition => condition),
				render: function () {
					const _costActualPerDay = getSelectedTableTabData(costActualPerDay, costActualWorkPerDay, costActualExpensesPerDay);
					const _costPlannedPerDay = getSelectedTableTabData(costPlannedPerDay, costPlannedWorkPerDay, costPlannedExpensesPerDay);
					const _pricePlannedPerDay = getSelectedTableTabData(pricePlannedPerDay, pricePlannedWorkPerDay, pricePlannedExpensesPerDay);
					const _priceActualPerDay = getSelectedTableTabData(priceActualPerDay, priceActualWorkPerDay, priceActualExpensesPerDay);
					const _priceRemainingPerDay = billable
						? getSelectedTableTabData(priceRemainingPerDay, priceRemainingWorkPerDay, priceRemainingExpensesPerDay)
						: getSelectedTableTabData(costRemainingPerDay, costRemainingWorkPerDay, costRemainingExpensesPerDay);
					const _costRemainingPerDay = getSelectedTableTabData(costRemainingPerDay, costRemainingWorkPerDay, costRemainingExpensesPerDay);

					return (
						<ProfitChart
							data={{
								costActualPerDay: _costActualPerDay,
								costPlannedPerDay: _costPlannedPerDay,
								costRemainingPerDay: _costRemainingPerDay,
								pricePlannedPerDay: _pricePlannedPerDay,
								priceActualPerDay: _priceActualPerDay,
								priceRemainingPerDay: _priceRemainingPerDay,
								project,
								labels,
							}}
							options={getChartOptions()}
							plugins={plugins}
							isEstimatedInNewPoints={isEstimatedInNewPoints}
							isFixedPrice={isFixedPrice}
						/>
					);
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.ACTUAL_VS_PLAN,
				name: formatMessage({id: 'project_budget.actual_vs_plan'}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.ACTUAL_VS_PLAN),
				shouldRender: [projectVariancesFilterOptions.actualVarianceToPlan, !this.props.isInvoiceTableTabSelected].every(condition => condition),
				render: function () {
					const actualPerDay = billable ? getSelectedTableTabData(priceActualPerDay, priceActualWorkPerDay, priceActualExpensesPerDay) : getSelectedTableTabData(costActualPerDay, costActualWorkPerDay, costActualExpensesPerDay);
					const forecastPerDay = billable ? getSelectedTableTabData(pricePlannedPerDay, pricePlannedWorkPerDay, pricePlannedExpensesPerDay) : getSelectedTableTabData(costPlannedPerDay, costPlannedWorkPerDay, costPlannedExpensesPerDay);
					return <ActualVarianceToPlanChart data={{isMoneyTabSelected, actualPerDay, forecastPerDay, registeredMinutesPerDay, plannedMinutesPerDay, project, labels}} options={getChartOptions()} plugins={plugins} />;
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.FORECAST_VS_PLAN,
				name: formatMessage({id: 'project_budget.forecast_vs_plan'}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.FORECAST_VS_PLAN),
				shouldRender: [projectVariancesFilterOptions.forecastVarianceToPlan, !this.props.isInvoiceTableTabSelected].every(condition => condition),
				render: function () {
					const remainingPerDay = billable
						? getSelectedTableTabData(priceRemainingPerDay, priceRemainingWorkPerDay, priceRemainingExpensesPerDay)
						: getSelectedTableTabData(costRemainingPerDay, costRemainingWorkPerDay, costRemainingExpensesPerDay);
					const actualPerDay = billable ? getSelectedTableTabData(priceActualPerDay, priceActualWorkPerDay, priceActualExpensesPerDay) : getSelectedTableTabData(costActualPerDay, costActualWorkPerDay, costActualExpensesPerDay);
					const forecastPerDay = billable ? getSelectedTableTabData(pricePlannedPerDay, pricePlannedWorkPerDay, pricePlannedExpensesPerDay) : getSelectedTableTabData(costPlannedPerDay, costPlannedWorkPerDay, costPlannedExpensesPerDay);

					return (
						<ForecastVarianceToPlanChart
							data={{
								isMoneyTabSelected,
								remainingPerDay,
								remainingMinutesPerDay,
								actualPerDay,
								forecastPerDay,
								registeredMinutesPerDay,
								plannedMinutesPerDay,
								estimationUnit,
								minutesPerEstimationPoint,
								project,
								labels,
							}}
							options={getChartOptions()}
							plugins={plugins}
						/>
					);
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.INVOICE,
				name: formatMessage({id: 'project_section.invoice'}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.INVOICE),
				shouldRender: [this.props.isInvoiceTableTabSelected].every(condition => condition),
				render: function () {
					return <InvoiceChart data={{invoicedPerDay, totalPaymentsPerDay, project, labels}} options={getChartOptions()} plugins={plugins} />;
				},
			},
			{
				id: BUDGET_V2_CHART_TYPE.INVOICE_VS_ACTUAL,
				name: formatMessage({id: 'project_budget.invoice_vs_actual'}),
				labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.INVOICE_VS_ACTUAL),
				shouldRender: [this.props.isInvoiceTableTabSelected].every(condition => condition),
				render: function () {
					return <InvoiceVarianceToActuals data={{invoicedPerDay, priceActualPerDay, project, labels}} options={getChartOptions()} plugins={plugins} />;
				},
			},
		];
		if (showBaseline) {
			this.charts.push(
				{
					id: BUDGET_V2_CHART_TYPE.BASELINE_VS_ACTUAL,
					name: formatMessage({id: 'project_budget.baseline_vs_actuals'}),
					labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.BASELINE_VS_ACTUAL),
					shouldRender: [projectVariancesFilterOptions.baselineVarianceToActuals, !this.props.isInvoiceTableTabSelected].every(condition => condition),
					render: function () {
						const baselinePerDay = billable
							? getSelectedTableTabData(baselinePricePerDay, baselinePriceWorkPerDay, baselinePriceExpensesPerDay)
							: getSelectedTableTabData(baselineCostPerDay, baselineCostWorkPerDay, baselineCostExpensesPerDay);
						const actualPerDay = billable ? getSelectedTableTabData(priceActualPerDay, priceActualWorkPerDay, priceActualExpensesPerDay) : getSelectedTableTabData(costActualPerDay, costActualWorkPerDay, costActualExpensesPerDay);
						return <BaselineVarianceToActuals data={{baselinePerDay, actualPerDay, project, labels}} options={getChartOptions()} plugins={plugins} />;
					},
				},
				{
					id: BUDGET_V2_CHART_TYPE.BASELINE_VS_FORECAST,
					name: formatMessage({id: 'project_budget.baseline_vs_forecast'}),
					labels: this.getChartLabels(BUDGET_V2_CHART_TYPE.BASELINE_VS_FORECAST),
					shouldRender: [projectVariancesFilterOptions.baselineVarianceToForecast, !this.props.isInvoiceTableTabSelected].every(condition => condition),
					render: function () {
						const baselinePerDay = billable
							? getSelectedTableTabData(baselinePricePerDay, baselinePriceWorkPerDay, baselinePriceExpensesPerDay)
							: getSelectedTableTabData(baselineCostPerDay, baselineCostWorkPerDay, baselineCostExpensesPerDay);
						const forecastPerDay = billable ? getSelectedTableTabData(pricePlannedPerDay, pricePlannedWorkPerDay, pricePlannedExpensesPerDay) : getSelectedTableTabData(costPlannedPerDay, costPlannedWorkPerDay, costPlannedExpensesPerDay);
						return <BaselineVarianceToForecast data={{baselinePerDay, forecastPerDay, project, labels}} options={getChartOptions()} plugins={plugins} />;
					},
				}
			);
		}

		if (!this.charts.some(chart => chart.id === activeChart)) {
			this.setState({activeChart: this.props.isMoneyTabSelected ? BUDGET_V2_CHART_TYPE.REVENUE : BUDGET_V2_CHART_TYPE.TIME});
		}

		return (
			<div className="chart" data-userpilot="budget-chart">
				{project.rateCard ? (
					<>
						<div className="chart-options" data-userpilot="budget-chart-tabs">
							{this.charts.map(item => {
								if (item.shouldRender) {
									return (
										<div key={item.id} onClick={this.chartClicked.bind(this, item.id)} className={`chart-option ${activeChart === item.id ? 'active' : ''}`}>
											{item.name}
										</div>
									);
								}

								return null;
							})}
						</div>
						{/* Render the active chart */}
						{activeChart !== null ? this.charts.find(chart => chart.id === activeChart)?.render() : null}
					</>
				) : (
					<GenericEmptyState callback={this.goToRateCard} actionText={formatMessage({id: 'project_budget.select_rates'})} />
				)}
			</div>
		);
	}
}

export default injectIntl(withRouter(BudgetCharts));
